import { CompositeChart } from '@mantine/charts'
import type { ComboboxData } from '@mantine/core'
import { ActionIcon, Group, Paper, Select, Stack, Tabs, Text, Title } from '@mantine/core'
import {
  IconChevronLeft,
  IconChevronRight,
  IconEyeExclamation,
  IconMessage,
  IconPencil,
  IconRuler2,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ResponsiveContainer } from 'recharts'

import { StatInfoGraphic } from '@/components/StatInfoGraphic'
import { useExplicitDateRange } from '@/hooks/useExplicitDateRange'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { GetSponsorTopicsResponse } from '@/services/api/api.types'
import { selectStyles } from '@/styles/Chart.css'
import {
  canNavigateToPreviousPeriod,
  conversationsSeries,
  formatChartDate,
  shareOfDayConversations,
} from '@/utils/analytics'

import { ConversationByDateTooltip } from './ConversationByDateTooltip'
import { NoDataAvailable } from './NoDataAvailable'
import TabChartWrapper from './TabChartWrapper'

const TabKeys = {
  conversationLineChart: 'conversationLineChart',
  messageLineChart: 'messageLineChart',
} as const

export default function TopicInsights() {
  const { api } = useGlobalState()

  const [selectedTopic, setSelectedTopic] = useState<GetSponsorTopicsResponse[0] | null>(null)
  const [syncedTopics, setSyncedTopics] = useState<GetSponsorTopicsResponse | null>(null)
  const [activeTab, setActiveTab] = useState<string | null>(TabKeys.conversationLineChart)
  const [backPeriod, setBackPeriod] = useState(0)

  const { data: sponsorInfo } = useQuery({
    queryKey: ['sponsor', 'info'],
    queryFn: () => api.getSponsorInfo(),
    staleTime: 5 * 60 * 1000,
  })

  const topicsQuery = useQuery({
    queryKey: ['sponsor', 'topics'],
    queryFn: api.getSponsorTopics,
    initialData: [],
  })

  const dateRange = useExplicitDateRange(backPeriod, sponsorInfo?.createdAt)

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'analytics',
      'topicConversationsByDate',
      selectedTopic?.id,
      backPeriod,
      dateRange.startDate,
      dateRange.endDate,
    ],
    queryFn: () =>
      api.getTopicConversationsByDate({
        topic_id: selectedTopic?.id ?? '',
        ...dateRange.analyticsParams,
      }),
    enabled: !!selectedTopic?.id,
  })

  const handlePreviousPeriod = () => setBackPeriod(prev => prev - 1)
  const handleNextPeriod = () => setBackPeriod(prev => prev + 1)

  const canGoPrevious = canNavigateToPreviousPeriod(backPeriod, sponsorInfo?.createdAt)
  const canGoNext = backPeriod < 0

  const topicOptions: ComboboxData = topicsQuery.data.map(topic => ({
    value: topic.id,
    label: topic.displayName,
    disabled: !topic.id,
  }))

  if (topicsQuery.data !== syncedTopics) {
    setSyncedTopics(topicsQuery.data)
    if (topicsQuery.data?.length > 0 && !selectedTopic) {
      setSelectedTopic(topicsQuery.data[0])
    }
  }

  const hasConversationData = (data?.data.length ?? 0) > 0

  return (
    <Stack>
      <Paper radius="md" p="lg" shadow="sm" px="xl">
        <Group justify="space-between" mb="md" align="top">
          <Select
            data={topicOptions}
            label="Topic"
            placeholder="Select a topic"
            value={selectedTopic?.id ?? null}
            onChange={value => {
              const topic = topicsQuery.data.find(topic => topic.id === value)
              setSelectedTopic(topic ?? null)
            }}
            miw={300}
            classNames={selectStyles}
          />

          <Stack align="flex-end" gap="md">
            <Group gap="xs" align="center">
              <ActionIcon
                variant="subtle"
                onClick={handlePreviousPeriod}
                disabled={!canGoPrevious}
                aria-label="Previous period">
                <IconChevronLeft size={20} />
              </ActionIcon>

              <Text size="sm" c="dimmed" style={{ minWidth: 180, textAlign: 'center' }}>
                {formatChartDate(dateRange.startDate, true)} — {formatChartDate(dateRange.endDate, true)}
              </Text>

              <ActionIcon variant="subtle" onClick={handleNextPeriod} disabled={!canGoNext} aria-label="Next period">
                <IconChevronRight size={20} />
              </ActionIcon>
            </Group>

            <Group gap="xl">
              <StatInfoGraphic
                icon={IconEyeExclamation}
                label="Unique Visitors"
                value={data?.stats.totalUniqueConversations ?? 0}
                tooltipText="Members who used this topic, counted once for each day they used it"
              />

              <StatInfoGraphic
                icon={IconMessage}
                label="Conversations"
                value={data?.stats.totalConversations ?? 0}
                tooltipText="Conversations in this topic, counted once for each day they were active"
              />

              <StatInfoGraphic
                icon={IconPencil}
                label="Member Messages"
                value={data?.stats.totalMessages ?? 0}
                tooltipText="Total number of messages sent by members using this topic"
              />

              <StatInfoGraphic
                icon={IconRuler2}
                label="Average Length"
                value={Math.round(data?.stats.averageConversationLength ?? 0)}
                tooltipText="Average member messages per conversation on each day it was active"
              />

              <StatInfoGraphic
                icon={IconRuler2}
                label="Max Length"
                value={data?.stats.maxConversationLength ?? 0}
                tooltipText="Most member messages in this topic on a single day"
              />
            </Group>
          </Stack>
        </Group>
      </Paper>

      <Paper radius="md" p="lg" shadow="sm" px="xl">
        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value={TabKeys.conversationLineChart}>Conversations</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value={TabKeys.conversationLineChart}>
            <Title order={6} mb="md" pt="md">
              Number of Conversations over Time
            </Title>
            <TabChartWrapper isActive={activeTab === TabKeys.conversationLineChart} isLoading={isLoading} error={error}>
              {hasConversationData ? (
                <ResponsiveContainer width="100%" height={300}>
                  <CompositeChart
                    data={data?.data ?? []}
                    dataKey="date"
                    curveType="linear"
                    series={conversationsSeries}
                    xAxisProps={{
                      tickFormatter: (date: string) => {
                        return formatChartDate(date)
                      },
                      angle: -45,
                      textAnchor: 'end',
                      interval: 0,
                    }}
                    tooltipProps={{
                      content: ({ payload, label }) => {
                        if (!payload || payload.length === 0) return null

                        return (
                          <ConversationByDateTooltip
                            label={label}
                            percentage={shareOfDayConversations(payload)}
                            payload={payload}
                          />
                        )
                      },
                    }}
                  />
                </ResponsiveContainer>
              ) : (
                <NoDataAvailable message="No conversation data currently available" />
              )}
            </TabChartWrapper>
          </Tabs.Panel>
        </Tabs>
      </Paper>
    </Stack>
  )
}
